import { captureException } from "extensions/sentry";
import { Component } from "react";
import { Tabs } from "extensions/react-responsive-tabs";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

import { FilterItem } from "./FilterItem";

export class TypeAheadResults extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeTab: 0,
            suggestions: props.suggestions
        };
    }
    
    componentDidMount() {
        document.addEventListener("keydown", this.checkKeyUpDown);
    }
    
    componentWillUnmount() {
        document.removeEventListener("keydown", this.checkKeyUpDown);
    }
    
    getSpecificData = () => {
        const allData = [["top", this.props.results.raw.slice(0, 6)], ...Object.entries(this.props.results.grouped)];
        return allData[this.state.activeTab][1];
    };
    
    checkKeyUpDown = (event) => {
        if (this.props.open) {
            if (event.keyCode === 38) { // Up Arrow
                const tabData = this.getSpecificData();
                const nextPosition = Math.max(this.state.suggestions[this.state.activeTab] - 1, 0);
                this.props.suggestionChanged(tabData[nextPosition], false);
                this.setState((prevState, props) => ({ suggestions: prevState.suggestions.map((s, index) => (index === prevState.activeTab ? nextPosition : s)) }));
            }
            if (event.keyCode === 40) { // Down Arrow
                const tabData = this.getSpecificData();
                const nextPosition = Math.min(this.state.suggestions[this.state.activeTab] + 1, tabData.length - 1);
                this.props.suggestionChanged(tabData[nextPosition], false);
                this.setState((prevState, props) => ({ suggestions: prevState.suggestions.map((s, index) => (index === prevState.activeTab ? nextPosition : s)) }));
            }
            if (event.keyCode === 13 || event.keyCode === 9) {
                this.props.suggestionChanged({ alias: "" }, false);
                const tabData = this.getSpecificData();
                const currentPosition = Math.max(this.state.suggestions[this.state.activeTab], 0);
                
                if (tabData[currentPosition]?.id) {
                    this.props.onFilterItemClick("+", tabData[currentPosition].id, false);
                } else if (tabData.length) {
                    captureException(new Error("Cannot find item after enter/tab key in typeahead results"));
                }
            }
            // Leave in case Bobby wants tab navigation without ever clicking.
            // if (event.keyCode === 9) {
            //     if (event.shiftKey) {
            //         this.onTabChange(Math.max((this.state.activeTab - 1), 0));
            //     } else {
            //         const v1 = this.state.activeTab + 1;
            //         const v2 = this.state.suggestions.length - 1;
            //         this.onTabChange(Math.min((this.state.activeTab + 1), (this.state.suggestions.length - 1)));
            //     }
            // }
        }
    };
    
    onTabChange = (tabIndex) => {
        if (this.state.activeTab !== tabIndex) {
            this.setState(
                { activeTab: tabIndex },
                () => {
                    const activeTabData = this.getSpecificData();
                    this.props.suggestionChanged(activeTabData[0], true);
                }
            );
        }
    };
    
    render() {
        const sortGroupedResults = Object.entries(this.props.results.grouped).sort((a, b) => (a[1].length < b[1].length ? -1 : 1));
        const getTopResults = () => {
            // get a cross section of results from first 6 tabs (or less)
            // Tab A : 1 result  Tab B : 1 result  Tab C : 2 results  Tab D : 3 results  Tab E : 4 results
            // Top results would be:A1,B1,C1,C2,D1,E1
            const topResults = [];
            const maxTabs = sortGroupedResults.length >= 6 ? 6 : sortGroupedResults.length;
            const maxResults = Math.min(this.props.results.raw.length, 6);
            const outerBound = Math.round(maxResults / maxTabs);
           
            for (let j = 0; j < outerBound; j++) {
                for (let i = 0; i < maxTabs; i++) {
                    if (sortGroupedResults[i][1][j] && topResults.length < maxResults) {
                        if (j > 0) {
                            const index = topResults.findIndex((r) => r.pivot === sortGroupedResults[i][1][j].pivot);
                            // keep items of same pivots togther
                            topResults.splice(index, 0, sortGroupedResults[i][1][j]);
                        } else {
                            topResults.push(sortGroupedResults[i][1][j]);
                        }
                    }
                }
            }
            
            return topResults;
        };
        
        const tabData = [
            ["top", getTopResults()],
            ...sortGroupedResults
        ];
       
        const getTitle = (item) => {
            const badgeNum = item[1].length > 24 ? "25+" : item[1].length;
            if (item[0] === "top") {
                return (
                    <div className="tab-content">
                        <div className="title">Top results</div>
                        <div className="spacer"/>
                    </div>
                );
            }
            return (
                <div className="tab-content">
                    <div className="title">{item[0]}</div>
                    <div className="numberCircle">{badgeNum}</div>
                    <div className="spacer"/>
                </div>
            );
        };
        
        const sendFilterAction = (action, item) => {
            this.props.onFilterItemClick(action, item, false);
        };
        
        // eslint-disable-next-line react/function-component-definition
        const rowRendererResults = (item, highlightIndex) => ({
            key, index, isScrolling, isVisible, style
        }) => (
            <FilterItem
                item={item[index]}
                key={`filter_item-${key}`}
                index={index}
                style={style}
                filterItemClick={sendFilterAction}
                allowExclude={this.props.allowExclude}
                isTypeAhead
                isHighlightedSuggestion={(index === highlightIndex)}
                isOmit={this.props.results.isOmit}
            />
        );
        
        const getTabContent = (filter) => {
            const item = filter[1];
            const resultsHeight = this.props.height - 100;
            const highlightIndex = this.state.suggestions[this.state.activeTab];
            
            return (
                // Need to add disableHeight flag here since we source height from props
                <AutoSizer disableHeight>
                    {({ width }) => (
                        <List
                            height={resultsHeight}
                            itemCount={item.length}
                            itemSize={30}
                            width={width}
                        >
                            {rowRendererResults(item, highlightIndex)}
                        </List>
                    )}
                </AutoSizer>
            );
        };
        
        const tabs = tabData.map((item, index) => ({
            title: getTitle(item),
            getContent: () => getTabContent(item),
            /* Optional parameters */
            key: index,
            tabClassName: "category-tab",
            panelClassName: "panel"
        }));
        
        return (
            <Tabs
                onChange={this.onTabChange}
                selectedTabKey={this.state.activeTab}
                tabsWrapperClass="categorized-results"
                items={tabs}
                transform={false}
            />
        );
    }
}
